import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from "react-router-dom";

import { Divider, Tab, Tabs } from "@mui/material";
import { 
  ModelBase,
} from "@models";
import { 
  SourceBook, 
} from "@dndModels";
import { 
  SOURCE_BOOK_TYPES 
} from '@dndModels/Constants';
import { 
  SourceBookType 
} from '@dndModels/Types';

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";

import ModelBaseInput from "@dndComponents/ModelBaseInput";
import CheckBox from "@components/input/CheckBox";
import SBSectionsEditor from "@dndComponents/SBSectionsEditor";
import { sourceBookReadURL } from "./read";
import SourceBookPdfImporter from '@components/input/Importer/SourceBookPdfImporter';
import ArrowBackButton from '@components/input/buttons/icon_buttons/ArrowBackButton';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import FlexBox from '@components/layout/FlexBox';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props {
  obj?: ModelBase;
}

// Non-admins can only make Homebrew source books, and those are always free.
const newSourceBook = () => {
  const new_obj = api.applyWorkingOn(new SourceBook());
  if (api.user && !api.user.admin) {
    new_obj.source_book_type = "Homebrew";
    new_obj.is_free = true;
  }
  return new_obj;
}

export default function SourceBookEdit({ obj: param_obj }: Props) {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<SourceBook>(param_obj instanceof SourceBook ? param_obj : newSourceBook());
  const [change_time, setChanged] = useState(-1);
  const [tab, setTab] = useState(0);

  const is_admin = api.user !== null && api.user.admin;
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  const is_owned = api.user !== null && api.user.owned_sb_ids.includes(obj._id);

  // Read-only view for logged-in non-admin users with access
  const user = api.user;
  // Admins get the editor instead.
  const canViewContent = !(user !== null && user.admin) && api.canReadSourceBook(obj);

  // Special sections build their subsections when they're first opened (see SBSectionsViewer).
  const load = useCallback( async () => {
    await api.loadObject(obj);
    setLoading(false);
  }, [obj]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  const renderPurchase = () => {
    if (obj.is_free) {
      return null;
    } else {
      const user = api.user;
      if (user) {
        if (user.owned_sb_ids.includes(obj._id)) {
          return null;
        } else if (user.purchased_sb_ids.includes(obj._id)) {
          return (
            <FlexBox direction="row">
              <FlexBox xs_expand={0} style={{ width: "50%" }}>
                You have purchased this source book
              </FlexBox>
              <FlexBox xs_expand={0} style={{ width: "50%" }}>
                <ButtonBox 
                  name="Unpurchase this Source Book"
                  disabled={processing || !api.online}
                  onClick={() => {
                    const user = api.user;
                    if (user) {
                      setProcessing(true);
                      user.purchased_sb_ids = user.purchased_sb_ids.filter(o => o !== obj._id);
                      api.updateObject("user", user).then(res => {
                        setProcessing(false);
                      });
                    }
                  }}
                />
              </FlexBox>
            </FlexBox>
          );
        } else if (user.shared_sb_ids.includes(obj._id)) {
          return [
            <FlexBox key="message">
              This source book has been shared with you, but you can still purchase it
            </FlexBox>,
            renderPurchaseButton()
          ];
        } else {
          return renderPurchaseButton();
        }
      } else {
        return (
          <FlexBox>You need to log in to be able to purchase a source book.</FlexBox>
        );
      }
    }
  }

  const renderPurchaseButton = () => {
    return (
      <FlexBox key="purchase">
        <ButtonBox 
          name="Purchase this Source Book"
          disabled={processing || !api.online}
          onClick={() => {
            const user = api.user;
            if (user) {
              setProcessing(true);
              user.purchased_sb_ids.push(obj._id);
              api.updateObject("user", user).then(res => {
                setProcessing(false);
              });
            }
          }}
        />(Currently just sets it to purchased in the db. There are no charges.)
      </FlexBox>
    );
  }

  // A user's first source book becomes their default. Later ones can be made the default
  // when they're created, or from their page.
  const renderDefault = () => {
    const user = api.user;
    if (!user || disabled) {
      return null;
    } else if (obj._id === "") {
      if (user.owned_sb_ids.length === 0) {
        return (
          <FlexBox>This will be your default source book. Anything else you create will go in it.</FlexBox>
        );
      }
      return (
        <FlexBox>
          <CheckBox 
            name="Make this my default source book"
            value={obj.set_as_default}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.set_as_default = changed;
              setObj(new_obj);
              setChanged(common_util.get_time_number());
            }}
          />
        </FlexBox>
      );
    } else if (is_owned) {
      if (user.default_sb_id === obj._id) {
        return (
          <FlexBox>This is your default source book. Anything else you create will go in it.</FlexBox>
        );
      }
      return (
        <FlexBox>
          <ButtonBox 
            name="Make this my default source book"
            disabled={processing || !api.online}
            onClick={async () => {
              setProcessing(true);
              await api.setDefaultSourceBook(obj._id);
              setProcessing(false);
            }}
          />
        </FlexBox>
      );
    }
    return null;
  }

  if (!fetched || loading) {
    const height = window.innerHeight - 64;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  }

  const renderMain = () => (
    <div className="padded">
      <ModelBaseInput
        obj={obj}
        disabled={disabled}
        change_time={change_time}
        no_source
        no_scrollbar
        onChange={(changed: ModelBase) => {
          if (changed instanceof SourceBook) {
            const new_obj = obj;
            new_obj.copy(changed);
            setObj(new_obj);
          }
        }}>
        <FlexBox direction="column">
          <FlexBox>
            <StringBox 
              name="Abbreviation"
              value={obj.abbreviation}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.abbreviation = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          </FlexBox>
          <FlexBox>
            <SelectStringBox 
              name="Type"
              value={obj.source_book_type}
              options={SOURCE_BOOK_TYPES}
              onChange={(changed: SourceBookType) => {
                const new_obj = obj;
                new_obj.source_book_type = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          </FlexBox>
          <FlexBox>
            <CheckBox 
              name="Free"
              value={obj.is_free}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.is_free = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          </FlexBox>
          <FlexBox>
            { is_admin &&
              <CheckBox 
                name="Third Party"
                value={obj.third_party}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.third_party = changed;
                  setObj(new_obj);
                  setChanged(common_util.get_time_number());
                }}
              />
            }
          </FlexBox>
          <FlexBox>
            <CheckBox 
              name="Public"
              value={obj.is_public}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.is_public = changed;
                setObj(new_obj);
                setChanged(common_util.get_time_number());
              }}
            />
          </FlexBox>
          { renderDefault() }
        </FlexBox>
      </ModelBaseInput>
    </div>
  );

  if (!disabled) {
    return (
      <>
        <Tabs value={tab} onChange={(_, val) => setTab(val)} style={{ marginBottom: 16 }}>
          <Tab label="Main" />
          <Tab label="Content" />
          { api.true_user && api.true_user.admin && <Tab label="Import PDF" /> }
        </Tabs>

        { tab === 0 && renderMain() }

        { tab === 1 && (
          <SBSectionsEditor
            source_book={obj}
            sections={obj.sections}
          />
        )}

        { tab === 2 && (
          <SourceBookPdfImporter
            source_book={obj}
            onImported={() => {
              setObj(obj);
              setChanged(common_util.get_time_number());
            }}
          />
        )}
      </>
    );
  } else {
    return (
      <FlexBox direction="column">
        <FlexBox>
          <FlexBox xs_expand={0} direction="row">
            <FlexBox xs_expand={0}>
              <ArrowBackButton
                fab
                onClick={ () => {
                  setNavigateTo(`/${obj.data_type}`);
                }}
              />
            </FlexBox>
            <FlexBox className="vCentered padded">
              <span className="header">Name:&nbsp;</span> { obj.name }
            </FlexBox>
          </FlexBox>
          <FlexBox direction="row">
            <FlexBox xs_expand={0} style={{ width: "50%" }}>
              <FlexBox direction="column">
                <FlexBox>
                  <span className="header">Abbreviation:</span> { obj.abbreviation }
                </FlexBox>
                <FlexBox>
                  <span className="header">Type:</span> { obj.source_book_type }
                </FlexBox>
                <FlexBox>
                  { obj.is_free && 
                    "This Source Book is free"
                  }
                </FlexBox>
              </FlexBox>
            </FlexBox>
            <FlexBox>
              { canViewContent ? 
                <DandDButtonBox
                  name="Read Source Book"
                  onClick={() => setNavigateTo(sourceBookReadURL(obj))}
                />
              : renderPurchase() }
            </FlexBox>
          </FlexBox>
        </FlexBox>
      </FlexBox>
    );
  } 
}